import { createColumnHelper } from '@tanstack/react-table';
import { array } from 'fp-ts';
import { LocalizedCurrencyValue, LocalizedPercentage, lt } from '@mews/framework';
import { Body, Icon, IconName, Link, Stack } from '@optimus-web/core';
import { CancellationPolicyOverviewEntry } from '@mews-commander/data-access';
import { Dash } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import {
    CancellationPolicyTiming,
    FeeExtent,
    LocalizedCancellationPolicyBookablePeriod,
    LocalizedSelectedBookablePeriod,
} from '../../../components';
import { RowActionCell } from './RowActionCell';

const columnHelper = createColumnHelper<CancellationPolicyOverviewEntry>();

export const getCancellationPoliciesColumns = ({ onNameClick }: { onNameClick: (id: string) => void }) => [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => (
            <Link data-disable-click-away standalone variant="basic" onClick={() => onNameClick(props.row.id)}>
                <Stack spacing="100" itemAlignment="center">
                    {props.row.original.isPortfolioManaged ? <Icon value={IconName.CustomLink} size="small" /> : null}
                    <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                        {props.getValue() || UnicodeEntity.Dash}
                    </Body>
                </Stack>
            </Link>
        ),
    }),
    columnHelper.accessor('absoluteFee', {
        header: () => lt('GeneralFixedAmount'),
        cell: ({
            row: {
                original: { absoluteFee, absoluteFeeCurrencyCode },
            },
        }) => <LocalizedCurrencyValue value={{ value: absoluteFee, currency: absoluteFeeCurrencyCode }} />,
    }),
    columnHelper.accessor('relativeFee', {
        header: () => lt('GeneralPercentage'),
        cell: (props) => <LocalizedPercentage value={props.getValue()} options={{ maximumFractionDigits: 2 }} />,
    }),
    columnHelper.accessor('applicabilityType', {
        header: () => lt('Deadline'),
        cell: (props) => (
            <CancellationPolicyTiming
                applicabilityType={props.getValue()}
                applicabilityOffset={props.row.original.applicabilityOffset}
            />
        ),
    }),
    columnHelper.accessor('feeMaximumTimeUnits', {
        header: () => lt('CancellationPolicyMaxPeriods'),
        cell: (props) => {
            const { bookablePeriod } = props.row.original;
            const maxPeriodsCount = props.getValue();

            return maxPeriodsCount ? (
                <LocalizedSelectedBookablePeriod
                    bookablePeriod={bookablePeriod}
                    substitutes={{ count: maxPeriodsCount }}
                />
            ) : (
                <Dash />
            );
        },
    }),
    columnHelper.accessor('bookablePeriod', {
        header: () => lt('BookablePeriod'),
        cell: (props) => <LocalizedCancellationPolicyBookablePeriod bookablePeriod={props.getValue()} />,
    }),
    columnHelper.accessor('feeExtent', {
        header: () => lt('FeeExtent'),
        cell: (props) => {
            const feeExtent = props.getValue();
            return !array.isEmpty(feeExtent) ? (
                <FeeExtent bookablePeriod={props.row.original.bookablePeriod} feeExtent={feeExtent} />
            ) : (
                <Dash />
            );
        },
    }),
    columnHelper.accessor('rateGroupCount', {
        header: () => lt('RateGroupPlural'),
        cell: (props) => props.getValue() || <Dash />,
    }),
    columnHelper.accessor('key', {
        header: () => lt('Key'),
        cell: (props) => props.getValue() || <Dash />,
    }),
    columnHelper.display({
        id: 'actions',
        meta: { align: 'end' },
        cell: (props) =>
            !props.row.original.isPortfolioManaged ? (
                <RowActionCell cancellationPolicyId={props.row.original.id} />
            ) : null,
    }),
];
